import type { OrderDetailResponse } from '@/types/orderDetails'

/**
 * Vérifie si un pays est HCEE (Hors CEE)
 * @param idPays ID du pays
 * @param isCee Indicateur si le pays est dans la CEE
 * @returns true si le pays est HCEE, false sinon
 */
export function isHCEE(idPays: number | undefined, isCee: boolean | null | undefined): boolean {
  if (!idPays) return true // Par défaut, considérer comme HCEE si pas de pays
  // Si is_cee est true, le pays est dans la CEE donc pas HCEE
  // Si is_cee est false ou null, le pays est HCEE
  return isCee !== true
}

/**
 * Calcule le taux de TVA selon la date de la commande
 * @param dateCommande Date de la commande (format YYYY-MM-DD ou YYYY-MM-DD HH:mm:ss)
 * @returns Taux de TVA (0.2 pour 20% depuis 2014, 0.196 pour 19.6% avant)
 */
export function calculateTVARate(dateCommande: string | null | undefined): number {
  if (!dateCommande) {
    // Par défaut, utiliser le taux actuel (20%)
    return 0.2
  }
  
  // Extraire l'année et le mois (format YYYY-MM)
  const dateStr = dateCommande.substring(0, 7) // "YYYY-MM"
  
  // Si la date est avant ou égale à 2013-12, taux à 19.6%
  if (dateStr <= '2013-12') {
    return 0.196
  }
  
  // Sinon, taux à 20%
  return 0.2
}

/**
 * Détermine si le client est étranger (ne paye pas la TVA)
 * Logique basée sur IsEtrangerNew() et IsEtranger() de l'ancien système
 * Pour les commandes normales : commande2.class.php ligne 3126
 * Pour les groupages : commandesgroupes.class.php ligne 3145
 * @param data Données de la commande
 * @returns true si le client est étranger (ne paye pas TVA), false sinon
 */
export function isEtranger(data: OrderDetailResponse): boolean {
  const activeTva = data.acheteur?.active_tva
  
  // Récupérer les pays de livraison et facturation
  // Note: Dans l'ancien système PHP, les comparaisons font automatiquement la conversion de type
  // En JavaScript/TypeScript, il faut convertir explicitement en number pour être sûr
  // car les valeurs peuvent être des strings ("10") ou des numbers (10)
  const idPaysLivraison = data.adresseLivraison?.pays?.id_pays
  const idPaysFacturation = data.adresseFacturation?.pays?.id_pays
  const isCeeLivraison = data.adresseLivraison?.pays?.is_cee
  const isCeeFacturation = data.adresseFacturation?.pays?.is_cee
  
  // Convertir en numbers pour être sûr (gère les cas où les valeurs sont des strings)
  const idPaysLivraisonNum = idPaysLivraison != null ? Number(idPaysLivraison) : null
  const idPaysFacturationNum = idPaysFacturation != null ? Number(idPaysFacturation) : null
  
  // 1. Si active_tva == 1, le client paye la TVA (pas étranger)
  if (activeTva === 1) {
    return false
  }
  
  // 2. Si le pays de livraison OU facturation est la France (id_pays = 10) ou Monaco (id_pays = 31), paye la TVA
  // Pour les groupages, l'ancien système vérifie aussi Monaco (31)
  if (idPaysLivraisonNum === 10 || idPaysFacturationNum === 10 || 
      idPaysLivraisonNum === 31 || idPaysFacturationNum === 31) {
    return false
  }
  
  // 3. Si les deux pays (livraison ET facturation) sont HCEE, alors étranger
  // Note: Si les pays ne sont pas définis, isHCEE retourne true (considéré comme HCEE)
  // donc si les deux sont undefined, ils seront considérés comme HCEE et étranger
  // MAIS dans l'ancien système, si les deux sont vides, on arrive à la condition 4
  const isHCEElivraison = isHCEE(idPaysLivraisonNum ?? undefined, isCeeLivraison)
  const isHCEEfacturation = isHCEE(idPaysFacturationNum ?? undefined, isCeeFacturation)
  
  // Vérifier que les deux pays sont définis avant de vérifier s'ils sont HCEE
  // (dans l'ancien système, on vérifie isset() avant d'appeler isHCEE)
  if (idPaysLivraisonNum != null && idPaysFacturationNum != null && 
      isHCEElivraison && isHCEEfacturation) {
    return true
  }
  
  // 4. Si le pays de livraison n'est PAS HCEE (donc dans la CEE) et active_tva == 0, alors étranger
  // Dans l'ancien système: elseif (!(Utils::isHCEE($dataAcheteur["id_pays_livraison"])))
  // Si idPaysLivraison est undefined, isHCEE retourne true (HCEE), donc !isHCEE retourne false
  // et cette condition ne s'applique pas, on passe à la condition 5
  if (idPaysLivraisonNum != null && !isHCEElivraison && activeTva === 0) {
    return true
  }
  
  // 5. Sinon, pas étranger (paye la TVA)
  // C'est le cas par défaut si les données sont manquantes ou si aucune condition précédente ne s'applique
  return false
}

